import React, { useEffect, useId, useRef, useState } from "react";
import { BookmarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { Note, NotesError, useNotes } from "./NotesContext";
import styles from "../components/ThreadContent.module.css";

export default function SaveNoteButton({ messageId, savedNoteId }: { messageId: string; savedNoteId?: string | null }) {
  const { user } = useAuthContext();
  const notes = useNotes();
  // This component is only mounted inside the app Router.
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();
  const busy = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  if (!user || !notes) return null;
  const noteId = Object.prototype.hasOwnProperty.call(notes.saved, messageId) ? notes.saved[messageId] : savedNoteId;
  const act = async () => {
    if (busy.current) return;
    busy.current = true; setPending(true); setError("");
    try {
      if (noteId) {
        await notes.request<Note>(`/${noteId}`);
        if (alive.current) navigate(`/notes/${noteId}`);
      } else {
        const note = await notes.request<Note>("", { method: "POST", body: JSON.stringify({ source_message_id: messageId }) });
        notes.remember(note);
      }
    } catch (e) {
      if (!alive.current || (e as Error).name === "AbortError") return;
      if (e instanceof NotesError && e.status === 404 && noteId) {
        notes.forget(messageId); setError("This note was deleted. You can save the answer again.");
      } else setError((e as Error).message);
    } finally { busy.current = false; if (alive.current) setPending(false); }
  };
  return <>
    <button
      className={`${styles.messageActionButton} ${styles.saveNoteButton} ${noteId ? styles.savedNoteButton : ""}`}
      type="button"
      onClick={act}
      disabled={pending}
      aria-label={noteId ? "View note" : "Save answer to notes"}
      aria-describedby={error ? errorId : undefined}
      title={noteId ? "Open your saved note" : "Save a private copy to your notes"}
    >
      {pending
        ? <span className={`${styles.statusSpinner} ${styles.noteActionSpinner}`} aria-hidden="true" />
        : <BookmarkIcon fill={noteId ? "currentColor" : "none"} aria-hidden="true" />}
      <span aria-live="polite">{pending ? noteId ? "Opening…" : "Saving…" : error ? "Try again" : noteId ? "View note" : "Save to notes"}</span>
    </button>
    {error && <p className={styles.noteActionError} id={errorId} role="alert">{error}</p>}
  </>;
}
